import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import { PageTitle, Panel } from "../components/Panel";

const UPDATED = "9 September 2026";
const CONTACT_EMAIL = "inquiry.longview@gmail.com";

const ContactEmail = ({ className = "text-amber-300 underline decoration-amber-500/40 underline-offset-4 hover:text-amber-200" }) => (
  <a href={`mailto:${CONTACT_EMAIL}`} className={className}>{CONTACT_EMAIL}</a>
);

const InfoPage = ({ kicker, title, subtitle, children }) => (
  <article className="mx-auto max-w-4xl">
    <PageTitle kicker={kicker} title={title} subtitle={subtitle} />
    <Panel topEdge className="space-y-8 p-5 sm:p-8">
      {children}
    </Panel>
  </article>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-semibold tracking-tight text-white sm:text-xl">{title}</h2>
    <div className="space-y-3 text-sm leading-7 text-zinc-400">{children}</div>
  </section>
);

const PolicyNote = () => (
  <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.05] p-4 text-sm leading-6 text-amber-100/90">
    <span className="font-semibold text-amber-200">Financial-information disclaimer.</span>{" "}
    LONGVIEW provides financial information and research tools for informational and educational
    purposes and is not investment, financial, legal, or tax advice.
  </div>
);

export function About() {
  return (
    <InfoPage
      kicker="About LONGVIEW"
      title="See the business behind the stock."
      subtitle="An India-focused financial-intelligence product built to make company research clearer for people learning to invest."
    >
      <Section title="What LONGVIEW is">
        <p>
          LONGVIEW brings company identity, historical end-of-day market data, structured company
          events, research context, and explanation tools into one focused experience. It is designed
          to help users look past a ticker and understand the business, its financial context, and the
          evidence worth investigating further.
        </p>
      </Section>
      <Section title="Why it exists">
        <p>
          Financial information is often fragmented, overly technical, or presented like a trading
          terminal. LONGVIEW organises the available evidence into an accessible research workflow for
          Indian companies without presenting itself as a broker or a source of personalised advice.
        </p>
      </Section>
      <Section title="How to use it">
        <p>
          Start with Discover, open a company, inspect its price history and source metadata, then use
          company events and LONGVIEW explanations as prompts for further research. Data coverage and
          freshness are shown where the underlying service provides them.
        </p>
        <p>Questions about LONGVIEW can be sent to <ContactEmail />.</p>
      </Section>
      <PolicyNote />
    </InfoPage>
  );
}

const FAQS = [
  {
    question: "What is LONGVIEW?",
    answer: "LONGVIEW is an India-focused company-research and financial-intelligence product. It combines structured company information, historical market context, company events, and explanatory research tools.",
  },
  {
    question: "Who is LONGVIEW for?",
    answer: "It is designed for people who want to understand businesses and public financial information, including beginners who do not need a real-time trading terminal.",
  },
  {
    question: "Where does market data come from?",
    answer: "The current development environment reads stored historical end-of-day data classified as Yahoo Finance development sandbox data. LONGVIEW displays source, classification, and freshness metadata where available. It is not real-time or licensed production market data.",
  },
  {
    question: "How does LONGVIEW use company events?",
    answer: "Company events are structured records with dates and source attribution. Current development event coverage uses company disclosures disseminated through NSE; source attribution does not mean NSE independently verified the disclosure.",
  },
  {
    question: "What does “Why It Matters” mean?",
    answer: "It is a bounded research interpretation of a selected event using the event and the company context available to LONGVIEW. It is not a prediction or recommendation, and limitations should be read alongside the explanation.",
  },
  {
    question: "Is LONGVIEW investment advice?",
    answer: "No. LONGVIEW is for informational and educational research. It does not provide personalised investment, financial, legal, or tax advice, price targets, or buy and sell recommendations.",
  },
  {
    question: "How current is the information?",
    answer: "Market prices are end-of-day and may be delayed or stale. Event and company coverage depends on the evidence currently stored by LONGVIEW. Check the displayed source and freshness details before relying on any item.",
  },
  {
    question: "Does LONGVIEW cover every Indian company?",
    answer: "No. The current product covers a deliberately limited LONGVIEW company universe. An empty event or intelligence section means LONGVIEW has no qualifying record in its current coverage, not that nothing happened in the real world.",
  },
  {
    question: "How can users contact LONGVIEW?",
    answer: <>Email <ContactEmail />.</>,
  },
];

export function FAQ() {
  return (
    <InfoPage
      kicker="LONGVIEW Guide"
      title="Frequently asked questions"
      subtitle="Practical answers about coverage, data, events, and how to interpret LONGVIEW research."
    >
      <div className="divide-y divide-white/[0.07]">
        {FAQS.map((item) => (
          <details key={item.question} className="group py-4 first:pt-0 last:pb-0">
            <summary className="cursor-pointer list-none pr-8 text-sm font-semibold text-white marker:hidden">
              {item.question}
              <span className="float-right font-mono text-amber-400" aria-hidden="true">+</span>
            </summary>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-zinc-400">{item.answer}</p>
          </details>
        ))}
      </div>
      <PolicyNote />
    </InfoPage>
  );
}

export function Privacy() {
  return (
    <InfoPage
      kicker="Legal"
      title="Privacy Policy"
      subtitle={`Last updated ${UPDATED}. This policy describes the data practices implemented in the current LONGVIEW application.`}
    >
      <PolicyNote />
      <Section title="Information collected">
        <p>
          LONGVIEW currently has no user-account or payment system. Information users voluntarily submit
          includes company searches, selected research questions, and portfolio-simulator entries such as
          ticker, quantity, and cost basis. The current simulator is not linked to a brokerage account.
        </p>
        <p>
          Browsers and application servers necessarily exchange technical request information to deliver
          pages and API responses. The repository does not implement a separate user-profile database or a
          defined store of device identifiers.
        </p>
      </Section>
      <Section title="How information is used">
        <p>
          Submitted inputs are used to return searches, research explanations, comparisons, and simulated
          portfolio calculations. Portfolio-simulator entries are stored in the configured LONGVIEW MongoDB
          database so the feature can work between requests. They must not be treated as private account data
          on a shared deployment because the current application has no account isolation.
        </p>
      </Section>
      <Section title="Cookies and local storage">
        <p>
          LONGVIEW does not require advertising cookies. If optional analytics is configured, your accept or
          reject choice is saved in local storage. The privacy-conscious analytics configuration uses memory
          persistence and does not enable session recording. Essential application functionality remains
          available if analytics is rejected.
        </p>
      </Section>
      <Section title="Analytics">
        <p>
          Optional PostHog page-view analytics can be enabled by a deployment operator only through environment
          configuration and only after consent. It records route paths without query strings. Autocapture,
          session recording, and person profiles are disabled; portfolio holdings and AI questions are not sent
          as analytics event properties. If analytics is not configured, no consent prompt is shown and the
          analytics client is not loaded.
        </p>
      </Section>
      <Section title="Third-party services">
        <p>
          LONGVIEW fetches fonts from Google Fonts. The backend may use attributed market-data, company-event,
          and server-side AI services described by source metadata in the product. When an AI explanation is
          requested, bounded company or event context may be processed server-side; provider credentials never
          need to be sent to the browser. External source links are opened only when a user follows them.
        </p>
      </Section>
      <Section title="Data retention">
        <p>
          The repository defines no universal retention period. Portfolio-simulator entries remain in the
          configured database until removed through the product or cleared by the deployment operator. Analytics
          retention, when enabled, is controlled by the operator's PostHog configuration and must be documented
          before a public launch.
        </p>
      </Section>
      <Section title="Security">
        <p>
          LONGVIEW keeps backend credentials out of frontend code and uses server-side API integrations. No
          internet service can guarantee absolute security. Do not enter confidential or sensitive personal
          information into search, research, or simulation features.
        </p>
      </Section>
      <Section title="Your rights and contact">
        <p>
          Privacy rights vary by location. To ask about information associated with this deployment, email{" "}
          <ContactEmail />.
        </p>
      </Section>
      <Section title="Children's privacy">
        <p>
          LONGVIEW is a financial-research product and is not designed specifically for children. It does not
          knowingly provide child accounts. A parent or guardian who believes a child submitted personal
          information should email <ContactEmail />.
        </p>
      </Section>
      <Section title="Changes to this policy">
        <p>
          This policy may be updated when the product's actual data practices change. The date at the top will be
          revised, and material changes should be reviewed before deployment.
        </p>
      </Section>
    </InfoPage>
  );
}

export function Terms() {
  return (
    <InfoPage
      kicker="Legal"
      title="Terms of Use"
      subtitle={`Last updated ${UPDATED}. These terms describe the intended use and present limitations of LONGVIEW.`}
    >
      <PolicyNote />
      <Section title="Acceptance of terms">
        <p>By accessing LONGVIEW, you agree to use the product consistently with these terms. If you do not agree, do not use the service.</p>
      </Section>
      <Section title="Description of LONGVIEW">
        <p>
          LONGVIEW is an India-focused financial-information, education, and company-research product. It is not
          a broker, exchange, investment adviser, portfolio manager, legal adviser, or tax adviser, and it does
          not execute transactions.
        </p>
      </Section>
      <Section title="Research, not investment advice">
        <p>
          Content, scores, event explanations, comparisons, simulations, and AI-assisted interpretations are
          general research tools. They do not account for your circumstances and must not be treated as a
          recommendation to buy, sell, or hold a security. Verify information independently before making a
          financial decision.
        </p>
      </Section>
      <Section title="Accuracy, completeness, and timeliness">
        <p>
          LONGVIEW does not guarantee that information is accurate, complete, uninterrupted, or current. Some
          company intelligence remains illustrative, coverage is limited, and missing records are not proof that
          no real-world event occurred. AI explanations may be unavailable or may state that evidence is
          insufficient.
        </p>
      </Section>
      <Section title="Market data and third-party sources">
        <p>
          The current development product uses stored end-of-day sandbox market data, not real-time licensed
          production data. Corporate disclosures and other source material retain their attribution. Attribution
          does not imply that LONGVIEW or the distributing platform independently verified every statement.
          Third-party content remains subject to its source's terms and rights.
        </p>
      </Section>
      <Section title="Acceptable use">
        <p>
          Do not interfere with the service, attempt to obtain credentials, bypass technical safeguards or rate
          limits, submit unlawful material, or use the product to misrepresent financial information. Automated
          access must respect applicable source terms and deployment controls.
        </p>
      </Section>
      <Section title="Intellectual property">
        <p>
          LONGVIEW's software, presentation, and original explanatory content may be protected by applicable
          intellectual-property rights. Company names, exchange information, source documents, and third-party
          data remain the property of their respective owners or licensors. These terms do not transfer ownership.
        </p>
      </Section>
      <Section title="External links">
        <p>
          Source links are provided for research and attribution. LONGVIEW does not control external sites and is
          not responsible for their availability, security, or content.
        </p>
      </Section>
      <Section title="Limitation of liability">
        <p>
          To the extent permitted by applicable law, LONGVIEW is provided on an as-available basis. Users remain
          responsible for verifying research and for their own decisions. Nothing in these terms excludes a right
          or responsibility that applicable law does not permit to be excluded.
        </p>
      </Section>
      <Section title="Changes and contact">
        <p>
          These terms may change as LONGVIEW's features and operating arrangements change. Questions can be sent
          to <ContactEmail />. These terms require human legal review before a public commercial launch.
        </p>
      </Section>
    </InfoPage>
  );
}

export function Contact() {
  return (
    <InfoPage
      kicker="Contact"
      title="Contact LONGVIEW"
      subtitle="Questions about LONGVIEW can be sent to the official public contact address below."
    >
      <div className="flex gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" aria-hidden="true" />
        <div className="space-y-2 text-sm leading-7 text-zinc-400">
          <h2 className="font-semibold text-white">Email LONGVIEW</h2>
          <p>
            <ContactEmail className="font-mono text-amber-300 underline decoration-amber-500/40 underline-offset-4 hover:text-amber-200" />
          </p>
          <p>This page does not pretend to submit a message or collect contact information.</p>
        </div>
      </div>
      <p className="text-xs leading-6 text-zinc-500">
        If you are following an external company-event source, the destination is operated by that source.
        <ExternalLink className="ml-1 inline h-3 w-3" aria-hidden="true" />
      </p>
      <PolicyNote />
    </InfoPage>
  );
}

export function NotFound() {
  return (
    <div className="mx-auto flex min-h-[58vh] max-w-2xl items-center justify-center text-center">
      <div>
        <div className="kicker mb-3">404 · Page not found</div>
        <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">This page isn't in view.</h1>
        <p className="mx-auto mt-4 max-w-lg text-sm leading-7 text-zinc-400">
          The address may have changed, or the page may not be part of the current LONGVIEW product.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" className="lv-press inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 font-mono text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/20">
            Back to LONGVIEW <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
          <Link to="/discover" className="lv-press rounded-md border border-white/[0.1] px-4 py-2 font-mono text-xs text-zinc-300 transition-colors hover:border-white/20 hover:text-white">
            Discover companies
          </Link>
        </div>
      </div>
    </div>
  );
}
